import { useState, useEffect } from 'react';
import API from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Send } from 'lucide-react';
import { toast } from 'sonner';
import { defaultEmail } from '@/lib/buyerMessaging';

export const PLACEHOLDER_HINT = '{{first_name}} {{buyer_name}} {{property_address}} {{price}} {{arv}} {{bedrooms}} {{bathrooms}} {{sqft}}';

export function EmailCampaignDialog({ open, onOpenChange, buyers, lead, onSent }) {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open) { const d = defaultEmail(lead); setSubject(d.subject); setBody(d.body); }
  }, [open, lead]);

  const noEmail = buyers.filter(b => !b.email).length;

  const send = async () => {
    if (!subject.trim() || !body.trim()) { toast.error('Subject and message are required'); return; }
    setSending(true);
    try {
      const res = await API.post('/buyer-intel/email-campaign', { buyer_ids: buyers.map(b => b.id), subject, html_content: body, lead_id: lead?.id || null });
      if (res.data.sent_count > 0) toast.success(res.data.message); else toast.error(res.data.message);
      onOpenChange(false); onSent?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send'); }
    finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-xl" data-testid="email-campaign-dialog">
        <DialogHeader><DialogTitle>Email {buyers.length} Buyer{buyers.length !== 1 ? 's' : ''}</DialogTitle></DialogHeader>
        <p className="text-xs text-gray-500">Sent from your connected Gmail, one personal email per buyer.{noEmail > 0 && <span className="text-amber-600"> {noEmail} selected buyer(s) have no email and will be skipped.</span>}</p>
        <div className="space-y-1.5">
          <Label className="text-xs text-gray-500">Subject</Label>
          <Input data-testid="email-campaign-subject" value={subject} onChange={e => setSubject(e.target.value)} className="bg-white border-gray-300" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs text-gray-500">Message</Label>
          <Textarea data-testid="email-campaign-body" value={body} onChange={e => setBody(e.target.value)} rows={9} className="bg-white border-gray-300" />
          <p className="text-[11px] text-gray-400">Placeholders: {PLACEHOLDER_HINT}</p>
        </div>
        <Button data-testid="email-campaign-send" onClick={send} disabled={sending} className="w-full bg-[#0073ea] hover:bg-[#0060c2] text-white">
          <Send className="h-4 w-4 mr-2" /> {sending ? 'Sending...' : `Send to ${buyers.length - noEmail} buyer(s)`}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
